import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate, useParams } from '@tanstack/react-router';
import { useEffect, useRef, useState } from 'react';
import type { HTMLAttributes } from 'react';

import { ApiError } from '@/api/client';
import {
  deleteEngagementFile,
  listEngagementFiles,
  uploadEngagementFile,
  type EngagementFileOut,
  type UploadableKind,
} from '@/api/engagementFiles';
import { EngagementCustomerVisibility } from '@/components/EngagementCustomerVisibility';
import {
  archiveEngagement,
  getEngagement,
  markPaid,
  markUnpaid,
  publishEngagement,
  setAgreePaymentUrl,
  setPortalState,
  unpublishEngagement,
  updateEngagement,
} from '@/api/engagements';
import { listRunsForEngagement } from '@/api/runs';
import { Badge } from '@/components/ui/badge';
import { Breadcrumbs } from '@/components/ui/breadcrumb';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { ErrorCard } from '@/components/ui/error-card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  deriveBillingView,
  isValidPaymentUrl,
  PORTAL_STATES,
  PORTAL_STATE_HINT,
  PORTAL_STATE_LABEL,
} from '@/lib/billing';
import {
  availableEngagementTabs,
  ENGAGEMENT_TAB_LABEL,
  resolveActiveTab,
  type EngagementTab,
} from '@/lib/engagementTabs';
import { formatCurrency, formatDate, formatDateTime } from '@/lib/format';
import { useAuth } from '../auth/AuthContext';
import { useCurrentRole } from '../auth/useCurrentRole';
import type {
  AuditRunOut,
  EngagementOut,
  EngagementStatus,
  PortalState,
  ProvinceCode,
} from '@/api/types';

const PROVINCE_CODES: readonly ProvinceCode[] = [
  'BC', 'AB', 'SK', 'MB', 'ON', 'QC', 'NB', 'NS', 'PE', 'NL', 'YT', 'NT', 'NU',
];
const STATUS_LABEL: Record<EngagementStatus, string> = {
  mnda_pending: 'MNDA pending',
  intake_pending: 'intake pending',
  engine_ready: 'engine ready',
  analyzing: 'analyzing',
  in_review: 'in review',
  evidence_pending: 'evidence pending',
  publishable: 'publishable',
  published: 'published',
};

const STATUS_BADGE_VARIANT: Record<
  EngagementStatus,
  { className?: string; variant?: 'outline' }
> = {
  mnda_pending: { variant: 'outline' },
  intake_pending: { variant: 'outline' },
  engine_ready: {
    className: 'border-transparent bg-amber-500 text-white hover:bg-amber-500/90',
  },
  analyzing: {
    className: 'border-transparent bg-brand-royal text-white hover:bg-brand-royal/90',
  },
  in_review: {
    className: 'border-transparent bg-brand-royal text-white hover:bg-brand-royal/90',
  },
  evidence_pending: {
    className: 'border-transparent bg-amber-500 text-white hover:bg-amber-500/90',
  },
  publishable: {
    className: 'border-transparent bg-emerald-600 text-white hover:bg-emerald-600/90',
  },
  published: {
    className: 'border-transparent bg-emerald-700 text-white hover:bg-emerald-700/90',
  },
};

export function EngagementDetailPage() {
  const { id } = useParams({ from: '/engagements/$id' });
  const { isSignedIn, userKey, getIdToken } = useAuth();
  const role = useCurrentRole();

  const engagement = useQuery({
    queryKey: ['engagements', id, userKey],
    enabled: isSignedIn,
    queryFn: async () => getEngagement(id, (await getIdToken() ?? '')),
  });

  const runs = useQuery({
    queryKey: ['runs', 'by-engagement', id, userKey],
    // Gate runs on the engagement load so a 404 short-circuits both queries
    // rather than firing the runs request against a non-existent engagement.
    enabled: isSignedIn && engagement.data !== undefined,
    queryFn: async () => listRunsForEngagement(id, (await getIdToken() ?? '')),
  });

  const [requestedTab, setRequestedTab] = useState<EngagementTab>('runs');

  if (!isSignedIn) {
    return (
      <Card className="max-w-xl">
        <CardHeader>
          <CardTitle>Not signed in</CardTitle>
        </CardHeader>
      </Card>
    );
  }

  if (engagement.isPending) return <p className="text-brand-slate">Loading…</p>;
  if (engagement.isError) return <ErrorCard title="Engagement not found" error={engagement.error} />;

  const e = engagement.data;
  const isPractitioner = role.isPractitioner;
  const isArchived = e.archived_at !== null;
  const canArchive = isPractitioner && !isArchived;
  const canRunAudit = isPractitioner && !isArchived;
  const canEdit = isPractitioner && !isArchived;

  const tabs = availableEngagementTabs({ isPractitioner, isArchived });
  const activeTab = resolveActiveTab(requestedTab, tabs);
  // An operator scanning engagements should see which ones are waiting on a
  // payment without opening each; flag the Billing tab while unpaid.
  const billingNeedsAttention = e.portal_state === 'awaiting_payment';

  // Only wire ARIA tab/panel semantics when the tab bar is actually shown.
  // A lone panel with no visible tablist would reference a hidden tab id.
  const tabbed = tabs.length > 1;
  const panelProps = (tab: EngagementTab): HTMLAttributes<HTMLDivElement> =>
    tabbed
      ? {
          role: 'tabpanel',
          id: `engagement-tabpanel-${tab}`,
          'aria-labelledby': `engagement-tab-${tab}`,
          tabIndex: 0,
        }
      : {};

  return (
    <div className="grid gap-6">
      <EngagementHeader
        engagement={e}
        canEdit={canEdit}
        canArchive={canArchive}
      />

      {tabbed && (
        <EngagementTabBar
          tabs={tabs}
          active={activeTab}
          onSelect={setRequestedTab}
          billingNeedsAttention={billingNeedsAttention}
        />
      )}

      {activeTab === 'runs' && (
        <div {...panelProps('runs')} className="outline-none">
          <section className="grid gap-3">
            <div className="flex items-baseline justify-between gap-3">
              <h3 className="text-base font-semibold text-brand-navy">Audit runs</h3>
              {canRunAudit && (
                <Button asChild type="button" size="sm">
                  <Link to="/engagements/$id/runs/new" params={{ id }}>
                    New run
                  </Link>
                </Button>
              )}
            </div>
            {runs.isPending ? (
              <p className="text-brand-slate">Loading runs…</p>
            ) : runs.isError ? (
              <ErrorCard title="Couldn't load runs" error={runs.error} />
            ) : runs.data.items.length === 0 ? (
              <Card className="max-w-xl">
                <CardHeader>
                  <CardTitle>No runs yet</CardTitle>
                  <CardDescription>
                    {canRunAudit
                      ? 'Use “New run” above to upload a source file and kick off the first one.'
                      : 'A practitioner will run audits against this engagement.'}
                  </CardDescription>
                </CardHeader>
              </Card>
            ) : (
              <AuditRunsTable
                engagement={e}
                runs={runs.data.items}
                canPublish={isPractitioner && !isArchived}
              />
            )}
          </section>
        </div>
      )}

      {activeTab === 'documents' && (
        <div {...panelProps('documents')} className="grid gap-6 outline-none">
          {!isArchived && <EngagementFilePool engagementId={id} />}
          <EngagementCustomerVisibility engagementId={id} customerSlug={e.customer_slug} />
        </div>
      )}

      {activeTab === 'billing' && (
        <div {...panelProps('billing')} className="outline-none">
          <EngagementBilling engagement={e} />
        </div>
      )}
    </div>
  );
}

function EngagementTabBar({
  tabs,
  active,
  onSelect,
  billingNeedsAttention,
}: {
  tabs: EngagementTab[];
  active: EngagementTab;
  onSelect: (tab: EngagementTab) => void;
  billingNeedsAttention: boolean;
}) {
  const tabRefs = useRef<Record<EngagementTab, HTMLButtonElement | null>>({
    runs: null,
    documents: null,
    billing: null,
  });

  const focusTab = (tab: EngagementTab) => {
    onSelect(tab);
    tabRefs.current[tab]?.focus();
  };

  return (
    <div role="tablist" aria-label="Engagement sections" className="flex gap-1 border-b border-border">
      {tabs.map((tab) => {
        const isActive = tab === active;
        const showDot = tab === 'billing' && billingNeedsAttention;
        return (
          <button
            key={tab}
            ref={(el) => {
              tabRefs.current[tab] = el;
            }}
            type="button"
            role="tab"
            id={`engagement-tab-${tab}`}
            aria-selected={isActive}
            aria-controls={`engagement-tabpanel-${tab}`}
            tabIndex={isActive ? 0 : -1}
            onClick={() => onSelect(tab)}
            onKeyDown={(ev) => {
              const idx = tabs.indexOf(tab);
              if (ev.key === 'ArrowRight') focusTab(tabs[(idx + 1) % tabs.length]);
              else if (ev.key === 'ArrowLeft')
                focusTab(tabs[(idx - 1 + tabs.length) % tabs.length]);
              else if (ev.key === 'Home') focusTab(tabs[0]);
              else if (ev.key === 'End') focusTab(tabs[tabs.length - 1]);
              else return;
              ev.preventDefault();
            }}
            className={`relative -mb-px flex items-center gap-2 border-b-2 px-4 py-2 text-sm font-medium transition outline-none focus-visible:ring-2 focus-visible:ring-brand-royal ${
              isActive
                ? 'border-brand-royal text-brand-navy'
                : 'border-transparent text-brand-slate hover:text-brand-navy'
            }`}
          >
            {ENGAGEMENT_TAB_LABEL[tab]}
            {showDot && (
              <span
                className="inline-block h-2 w-2 rounded-full bg-amber-500"
                aria-label="Action required"
                title="Awaiting payment"
              />
            )}
          </button>
        );
      })}
    </div>
  );
}

function EngagementBilling({ engagement }: { engagement: EngagementOut }) {
  const { userKey, getIdToken } = useAuth();
  const qc = useQueryClient();
  const billing = deriveBillingView(engagement);
  const [urlInput, setUrlInput] = useState(engagement.agree_payment_url ?? '');
  const [confirmUnpaid, setConfirmUnpaid] = useState(false);

  // Resync the link field when the server value changes (background refetch,
  // another session editing) so the input never disagrees with the record.
  useEffect(() => {
    setUrlInput(engagement.agree_payment_url ?? '');
  }, [engagement.agree_payment_url]);

  const applyUpdate = (updated: EngagementOut) => {
    qc.setQueryData(['engagements', engagement.id, userKey], updated);
    qc.invalidateQueries({ queryKey: ['engagements', 'by-account'] });
  };

  const stateMutation = useMutation({
    mutationFn: async (next: PortalState) =>
      setPortalState(engagement.id, next, (await getIdToken()) ?? ''),
    onSuccess: applyUpdate,
  });

  const urlMutation = useMutation({
    mutationFn: async (next: string | null) =>
      setAgreePaymentUrl(engagement.id, next, (await getIdToken()) ?? ''),
    onSuccess: applyUpdate,
  });

  const paidMutation = useMutation({
    mutationFn: async () => markPaid(engagement.id, (await getIdToken()) ?? ''),
    onSuccess: applyUpdate,
  });

  const unpaidMutation = useMutation({
    mutationFn: async () => markUnpaid(engagement.id, (await getIdToken()) ?? ''),
    onSuccess: (updated) => {
      applyUpdate(updated);
      setConfirmUnpaid(false);
    },
  });

  // Each mutation replaces the whole cached engagement, so overlapping
  // billing writes could resolve out of order and clobber the cache with a
  // stale snapshot. Gate every control on a single shared pending flag.
  const billingMutationPending =
    stateMutation.isPending ||
    urlMutation.isPending ||
    paidMutation.isPending ||
    unpaidMutation.isPending;

  const urlValid = isValidPaymentUrl(urlInput);
  const urlDirty = urlInput.trim() !== (engagement.agree_payment_url ?? '');

  return (
    <section className="grid gap-3">
      <h3 className="text-base font-semibold text-brand-navy">Billing</h3>
      <Card>
        <CardHeader>
          <CardDescription>
            Controls the customer&apos;s portal experience: the billing state banner, the
            payment link, and whether the report and CSV exports are unlocked. Exports
            unlock once the engagement is published and marked paid.
          </CardDescription>
        </CardHeader>
        <CardContent className="grid gap-6">
          <div className="grid gap-2">
            <label className="text-sm font-medium text-brand-navy" htmlFor="portal-state">
              Engagement state
            </label>
            <select
              id="portal-state"
              value={billing.state}
              onChange={(ev) => stateMutation.mutate(ev.target.value as PortalState)}
              disabled={billingMutationPending}
              className="max-w-xs rounded-md border border-input bg-transparent px-3 py-2 text-sm shadow-xs outline-none focus-visible:ring-2 focus-visible:ring-brand-royal"
            >
              {PORTAL_STATES.map((s) => (
                <option key={s} value={s}>
                  {PORTAL_STATE_LABEL[s]}
                </option>
              ))}
            </select>
            <p className="text-xs text-brand-slate">{PORTAL_STATE_HINT[billing.state]}</p>
            {stateMutation.isError && (
              <span className="text-xs text-destructive">
                Couldn&apos;t update the state — see console.
              </span>
            )}
          </div>

          <div className="grid gap-2">
            <label className="text-sm font-medium text-brand-navy" htmlFor="payment-url">
              Payment link
            </label>
            <div className="flex flex-wrap items-center gap-2">
              <input
                id="payment-url"
                type="url"
                value={urlInput}
                onChange={(ev) => setUrlInput(ev.target.value)}
                placeholder="https://…"
                className="w-full max-w-md rounded-md border border-input bg-transparent px-3 py-2 text-sm shadow-xs outline-none focus-visible:ring-2 focus-visible:ring-brand-royal"
              />
              <Button
                type="button"
                size="sm"
                disabled={!urlValid || !urlDirty || billingMutationPending}
                onClick={() =>
                  urlMutation.mutate(urlInput.trim() === '' ? null : urlInput.trim())
                }
              >
                {urlMutation.isPending ? 'Saving…' : 'Save link'}
              </Button>
            </div>
            {!urlValid && (
              <span className="text-xs text-destructive">
                Must be an https link (or blank to clear).
              </span>
            )}
            {urlValid && !billing.hasPaymentLink && (
              <span className="text-xs text-brand-slate">
                No payment link set — the customer can&apos;t pay until one is added.
              </span>
            )}
            {urlMutation.isError && (
              <span className="text-xs text-destructive">
                Couldn&apos;t save the link — see console.
              </span>
            )}
          </div>

          <div className="grid gap-2">
            <span className="text-sm font-medium text-brand-navy">Invoice payment</span>
            {billing.isPaid ? (
              <div className="flex flex-wrap items-center gap-3">
                <Badge className="border-transparent bg-emerald-600 text-white">
                  Paid{engagement.invoice_paid_at
                    ? ` on ${formatDateTime(engagement.invoice_paid_at)}`
                    : ''}
                </Badge>
                {confirmUnpaid ? (
                  <ConfirmBar
                    message="Reverse the payment? The customer's exports lock again and the state moves back to awaiting payment."
                    confirmLabel="Yes, mark unpaid"
                    working={unpaidMutation.isPending}
                    onConfirm={() => unpaidMutation.mutate()}
                    onCancel={() => setConfirmUnpaid(false)}
                  />
                ) : (
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    onClick={() => setConfirmUnpaid(true)}
                    disabled={billingMutationPending}
                  >
                    Undo
                  </Button>
                )}
              </div>
            ) : (
              <div className="flex flex-wrap items-center gap-2">
                <Button
                  type="button"
                  size="sm"
                  onClick={() => paidMutation.mutate()}
                  disabled={billingMutationPending}
                >
                  {paidMutation.isPending ? 'Marking…' : 'Mark invoice paid'}
                </Button>
                <span className="text-xs text-brand-slate">
                  Do this when the payment-received email arrives.
                </span>
              </div>
            )}
            {(paidMutation.isError || unpaidMutation.isError) && (
              <span className="text-xs text-destructive">
                Couldn&apos;t update payment — see console.
              </span>
            )}
          </div>
        </CardContent>
      </Card>
    </section>
  );
}

function EngagementFilePool({ engagementId }: { engagementId: string }) {
  const { userKey, getIdToken } = useAuth();
  const qc = useQueryClient();
  const [isDragging, setIsDragging] = useState(false);
  const [kind, setKind] = useState<UploadableKind>('transactions');
  const inputRef = useRef<HTMLInputElement | null>(null);

  const files = useQuery({
    queryKey: ['engagementFiles', engagementId, userKey],
    queryFn: async () => listEngagementFiles(engagementId, (await getIdToken()) ?? ''),
  });

  const invalidateFiles = () => {
    qc.invalidateQueries({ queryKey: ['engagementFiles', engagementId, userKey] });
  };

  const upload = useMutation({
    mutationFn: async (file: File) =>
      uploadEngagementFile(engagementId, file, kind, (await getIdToken()) ?? ''),
    onSuccess: invalidateFiles,
  });

  const remove = useMutation({
    mutationFn: async (fileId: string) =>
      deleteEngagementFile(engagementId, fileId, (await getIdToken()) ?? ''),
    onSuccess: invalidateFiles,
  });

  const handleFiles = (list: FileList | null) => {
    if (!list) return;
    Array.from(list).forEach((f) => upload.mutate(f));
  };

  const openPicker = () => inputRef.current?.click();

  return (
    <section className="grid gap-3">
      <h3 className="text-base font-semibold text-brand-navy">Engagement files</h3>
      <Card>
        <CardHeader>
          <CardDescription>
            Drop the customer&apos;s GL export and any supplier file here. Files persist across
            audit runs. Uploading does not change the engagement&apos;s status — advance state
            explicitly when intake is complete.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="mb-3 flex items-center gap-2 text-sm">
            <label className="text-brand-slate" htmlFor="engagement-file-kind">
              Upload as:
            </label>
            <select
              id="engagement-file-kind"
              value={kind}
              onChange={(ev) => setKind(ev.target.value as UploadableKind)}
              className="rounded-md border border-input bg-transparent px-2 py-1 text-sm shadow-xs outline-none focus-visible:ring-2 focus-visible:ring-brand-royal"
            >
              <option value="transactions">Transactions (GL export)</option>
              <option value="suppliers">Suppliers (vendor master)</option>
            </select>
          </div>
          <div
            className={`flex flex-col items-center gap-2 rounded-md border-2 border-dashed p-6 text-center text-sm transition ${
              isDragging ? 'border-brand-royal bg-brand-royal/5' : 'border-border'
            }`}
            onDragOver={(ev) => {
              ev.preventDefault();
              setIsDragging(true);
            }}
            onDragLeave={() => setIsDragging(false)}
            onDrop={(ev) => {
              ev.preventDefault();
              setIsDragging(false);
              handleFiles(ev.dataTransfer.files);
            }}
          >
            <Button type="button" variant="outline" size="sm" onClick={openPicker}>
              Choose files
            </Button>
            <span className="text-xs text-brand-slate">or drop them here</span>
            {upload.isPending && <span className="text-xs text-brand-slate">Uploading…</span>}
            {upload.isError && (
              <span className="text-xs text-destructive">
                Upload failed —{' '}
                {upload.error instanceof ApiError
                  ? `HTTP ${upload.error.status}`
                  : 'see console.'}
              </span>
            )}
            <input
              ref={inputRef}
              type="file"
              // sr-only-ish: visually hidden but still keyboard-focusable
              // via the Choose-files button. `display:none` would also
              // remove it from the a11y tree.
              className="sr-only"
              onChange={(ev) => {
                handleFiles(ev.target.files);
                // Reset so picking the same file twice still fires onChange.
                ev.target.value = '';
              }}
              multiple
            />
          </div>

          {files.isPending ? (
            <p className="mt-3 text-sm text-brand-slate">Loading files…</p>
          ) : files.isError ? (
            <p className="mt-3 text-sm text-destructive">Couldn&apos;t load files.</p>
          ) : files.data.items.length === 0 ? (
            <p className="mt-3 text-sm text-brand-slate">No files uploaded yet.</p>
          ) : (
            <ul className="mt-3 grid gap-2">
              {files.data.items.map((f) => (
                <EngagementFileRow
                  key={f.id}
                  file={f}
                  onDelete={() => remove.mutate(f.id)}
                  deleting={remove.isPending}
                />
              ))}
            </ul>
          )}
        </CardContent>
      </Card>
    </section>
  );
}

function EngagementFileRow({
  file,
  onDelete,
  deleting,
}: {
  file: EngagementFileOut;
  onDelete: () => void;
  deleting: boolean;
}) {
  const sizeKb = (file.byte_count / 1024).toFixed(1);
  return (
    <li className="flex items-center justify-between gap-3 rounded-md border border-border bg-background px-3 py-2 text-sm">
      <div className="flex-1 min-w-0">
        <div className="truncate font-medium text-brand-navy">{file.filename}</div>
        <div className="text-xs text-brand-slate">
          {file.kind} · {sizeKb} KB · uploaded {formatDateTime(file.uploaded_at)}
        </div>
      </div>
      <Button type="button" size="sm" variant="ghost" onClick={onDelete} disabled={deleting}>
        {deleting ? 'Removing…' : 'Remove'}
      </Button>
    </li>
  );
}

function EngagementHeader({
  engagement,
  canEdit,
  canArchive,
}: {
  engagement: EngagementOut;
  canEdit: boolean;
  canArchive: boolean;
}) {
  const [editing, setEditing] = useState(false);

  if (editing) {
    return (
      <EditEngagementForm engagement={engagement} onClose={() => setEditing(false)} />
    );
  }

  return (
    <header>
      <Breadcrumbs>
        <Link to="/customers" className="text-brand-royal underline-offset-4 hover:underline">
          Customers
        </Link>
        <Link
          to="/customers/$slug"
          params={{ slug: engagement.customer_slug }}
          className="text-brand-royal underline-offset-4 hover:underline"
        >
          {engagement.customer_name}
        </Link>
        <span className="text-brand-navy">{engagement.name}</span>
      </Breadcrumbs>
      <div className="mt-2 flex items-baseline justify-between gap-3">
        <h2 className="text-2xl font-semibold tracking-tight text-brand-navy">
          {engagement.name}
        </h2>
        <div className="flex items-center gap-2">
          <EngagementStatusBadge status={engagement.status} />
          {engagement.archived_at && <Badge variant="secondary">archived</Badge>}
          {canEdit && (
            <Button
              type="button"
              size="sm"
              variant="outline"
              onClick={() => setEditing(true)}
            >
              Edit
            </Button>
          )}
          {canArchive && <ArchiveButton engagement={engagement} />}
        </div>
      </div>
      <dl className="mt-2 flex flex-wrap items-center gap-x-6 gap-y-1 text-sm text-brand-slate">
        <div className="flex items-center gap-1.5">
          <dt>period:</dt>
          <dd className="font-mono">
            {formatDate(engagement.period_start)} → {formatDate(engagement.period_end)}
          </dd>
        </div>
        <div className="flex items-center gap-1.5">
          <dt>province:</dt>
          <dd className="font-mono">{engagement.default_province ?? '—'}</dd>
        </div>
      </dl>
    </header>
  );
}

function EngagementStatusBadge({ status }: { status: EngagementStatus }) {
  const variant = STATUS_BADGE_VARIANT[status];
  if (variant.className) {
    return <Badge className={variant.className}>{STATUS_LABEL[status]}</Badge>;
  }
  return <Badge variant={variant.variant}>{STATUS_LABEL[status]}</Badge>;
}

function EditEngagementForm({
  engagement,
  onClose,
}: {
  engagement: EngagementOut;
  onClose: () => void;
}) {
  const { userKey, getIdToken } = useAuth();
  const qc = useQueryClient();
  const [name, setName] = useState(engagement.name);
  const [periodStart, setPeriodStart] = useState(engagement.period_start);
  const [periodEnd, setPeriodEnd] = useState(engagement.period_end);
  const [province, setProvince] = useState<ProvinceCode | ''>(
    engagement.default_province ?? '',
  );

  // Status is intentionally absent from this form. It flows through the
  // state machine via the Next-action card (`/advance`) and the Publish
  // panel (`/publish`). A status dropdown would let the practitioner
  // patch directly to `published` without an `audit_run_id`, bypassing
  // the publish contract — the backend rejects it (422) but exposing
  // the option here is a footgun.

  const save = useMutation({
    mutationFn: async () =>
      updateEngagement(
        engagement.id,
        {
          name: name.trim(),
          period_start: periodStart,
          period_end: periodEnd,
          // null clears, '' (form blank) maps to null; matching value omitted.
          default_province: province === '' ? null : province,
        },
        (await getIdToken() ?? ''),
      ),
    onSuccess: (updated) => {
      qc.setQueryData(['engagements', engagement.id, userKey], updated);
      qc.invalidateQueries({ queryKey: ['engagements', 'by-account'] });
      onClose();
    },
  });

  const submittable =
    name.trim().length > 0 && periodStart !== '' && periodEnd !== '';

  return (
    <Card className="w-full max-w-2xl">
      <CardHeader>
        <CardTitle>Edit engagement</CardTitle>
        <CardDescription>
          Updates take effect immediately. The audit run history is preserved.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <form
          className="grid gap-3"
          onSubmit={(ev) => {
            ev.preventDefault();
            if (!submittable) return;
            save.mutate();
          }}
        >
          <label className="grid gap-1 text-sm">
            <span className="text-brand-slate">Name</span>
            <input
              type="text"
              value={name}
              onChange={(ev) => setName(ev.target.value)}
              maxLength={256}
              required
              className="rounded-md border border-input bg-transparent px-3 py-2 text-sm shadow-xs outline-none focus-visible:ring-2 focus-visible:ring-brand-royal"
            />
          </label>
          <div className="grid grid-cols-2 gap-3">
            <label className="grid gap-1 text-sm">
              <span className="text-brand-slate">Period start</span>
              <input
                type="date"
                value={periodStart}
                onChange={(ev) => setPeriodStart(ev.target.value)}
                required
                className="rounded-md border border-input bg-transparent px-3 py-2 text-sm shadow-xs outline-none focus-visible:ring-2 focus-visible:ring-brand-royal"
              />
            </label>
            <label className="grid gap-1 text-sm">
              <span className="text-brand-slate">Period end</span>
              <input
                type="date"
                value={periodEnd}
                onChange={(ev) => setPeriodEnd(ev.target.value)}
                required
                className="rounded-md border border-input bg-transparent px-3 py-2 text-sm shadow-xs outline-none focus-visible:ring-2 focus-visible:ring-brand-royal"
              />
            </label>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <label className="grid gap-1 text-sm">
              <span className="text-brand-slate">
                Default province <span className="text-xs">(optional)</span>
              </span>
              <select
                value={province}
                onChange={(ev) => setProvince(ev.target.value as ProvinceCode | '')}
                className="rounded-md border border-input bg-transparent px-3 py-2 text-sm shadow-xs outline-none focus-visible:ring-2 focus-visible:ring-brand-royal"
              >
                <option value="">— none —</option>
                {PROVINCE_CODES.map((p) => (
                  <option key={p} value={p}>
                    {p}
                  </option>
                ))}
              </select>
            </label>
          </div>
          <div className="flex items-center gap-2">
            <Button type="submit" size="sm" disabled={save.isPending}>
              {save.isPending ? 'Saving…' : 'Save'}
            </Button>
            <Button
              type="button"
              size="sm"
              variant="ghost"
              disabled={save.isPending}
              onClick={onClose}
            >
              Cancel
            </Button>
            {save.isError && (
              <span className="text-xs text-destructive">
                {save.error instanceof ApiError
                  ? `HTTP ${save.error.status} — see console`
                  : 'Save failed — see console.'}
              </span>
            )}
          </div>
        </form>
      </CardContent>
    </Card>
  );
}

function AuditRunsTable({
  engagement,
  runs,
  canPublish,
}: {
  engagement: EngagementOut;
  runs: AuditRunOut[];
  canPublish: boolean;
}) {
  const { userKey, getIdToken } = useAuth();
  const qc = useQueryClient();
  const publishedRunId = engagement.published_audit_run_id ?? null;
  const [selectedRunId, setSelectedRunId] = useState<string | null>(publishedRunId);
  const [pendingAction, setPendingAction] = useState<'publish' | 'unpublish' | null>(null);

  // Resync the selection when the server-side published run changes
  // (background refetch, another session publishing/unpublishing) so the
  // radio and the "Published" pill never disagree.
  useEffect(() => {
    setSelectedRunId(publishedRunId);
  }, [publishedRunId]);

  const invalidate = (updated: EngagementOut) => {
    qc.setQueryData(['engagements', engagement.id, userKey], updated);
    qc.invalidateQueries({ queryKey: ['engagements', 'by-account'] });
  };

  const publish = useMutation({
    mutationFn: async (runId: string) =>
      publishEngagement(engagement.id, runId, (await getIdToken()) ?? '', 'Practitioner-published'),
    onSuccess: (updated) => {
      invalidate(updated);
      setPendingAction(null);
    },
  });

  const unpublish = useMutation({
    mutationFn: async () => unpublishEngagement(engagement.id, (await getIdToken()) ?? ''),
    onSuccess: (updated) => {
      invalidate(updated);
      setPendingAction(null);
    },
  });

  const selectedRun = runs.find((r) => r.id === selectedRunId) ?? null;
  const isSelectedAlreadyPublished =
    selectedRunId !== null && selectedRunId === publishedRunId;
  const publishDisabled =
    !canPublish ||
    selectedRun === null ||
    selectedRun.status !== 'completed' ||
    isSelectedAlreadyPublished;

  return (
    <div className="grid gap-2">
      {canPublish && (
        <div className="flex flex-wrap items-center gap-2">
          <Button
            type="button"
            size="sm"
            onClick={() => setPendingAction('publish')}
            disabled={publishDisabled || publish.isPending}
          >
            {publishedRunId ? 'Publish selected run' : 'Publish'}
          </Button>
          {publishedRunId && (
            <Button
              type="button"
              size="sm"
              variant="outline"
              onClick={() => setPendingAction('unpublish')}
              disabled={unpublish.isPending}
            >
              Unpublish
            </Button>
          )}
          {selectedRun && selectedRun.status !== 'completed' && (
            <span className="text-xs text-brand-slate">
              Only completed runs can be published.
            </span>
          )}
          {isSelectedAlreadyPublished && (
            <span className="text-xs text-brand-slate">This run is already published.</span>
          )}
          {publish.isError && (
            <span className="text-xs text-destructive">
              {publish.error instanceof ApiError
                ? `Publish failed (HTTP ${publish.error.status})`
                : 'Publish failed — see console.'}
            </span>
          )}
          {unpublish.isError && (
            <span className="text-xs text-destructive">
              {unpublish.error instanceof ApiError
                ? `Unpublish failed (HTTP ${unpublish.error.status})`
                : 'Unpublish failed — see console.'}
            </span>
          )}
        </div>
      )}

      {pendingAction === 'publish' && selectedRun && (
        <ConfirmBar
          message={
            publishedRunId
              ? `Replace the currently published run with the one started ${formatDateTime(selectedRun.started_at)}?`
              : `Publish the run started ${formatDateTime(selectedRun.started_at)} to the customer portal?`
          }
          confirmLabel="Yes, publish this run"
          working={publish.isPending}
          onConfirm={() => publish.mutate(selectedRun.id)}
          onCancel={() => setPendingAction(null)}
        />
      )}
      {pendingAction === 'unpublish' && (
        <ConfirmBar
          message="Unpublish the current run? The customer portal will go back to 'report being prepared' until you publish again."
          confirmLabel="Yes, unpublish"
          working={unpublish.isPending}
          onConfirm={() => unpublish.mutate()}
          onCancel={() => setPendingAction(null)}
        />
      )}

      <Table>
        <TableHeader>
          <TableRow>
            {canPublish && <TableHead className="w-8"></TableHead>}
            <TableHead>Run</TableHead>
            <TableHead>Status</TableHead>
            <TableHead>Rule set</TableHead>
            <TableHead className="text-right">Transactions</TableHead>
            <TableHead className="text-right">Findings</TableHead>
            <TableHead className="text-right">Reviewed</TableHead>
            <TableHead className="text-right text-emerald-700">Approved recovery</TableHead>
            <TableHead className="text-right text-amber-700">Approved compliance</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {runs.map((r) => (
            <AuditRunsTableRow
              key={r.id}
              run={r}
              canPublish={canPublish}
              isSelected={selectedRunId === r.id}
              isPublished={publishedRunId === r.id}
              onSelect={() => setSelectedRunId(r.id)}
            />
          ))}
        </TableBody>
      </Table>
    </div>
  );
}

function AuditRunsTableRow({
  run: r,
  canPublish,
  isSelected,
  isPublished,
  onSelect,
}: {
  run: AuditRunOut;
  canPublish: boolean;
  isSelected: boolean;
  isPublished: boolean;
  onSelect: () => void;
}) {
  const selectable = r.status === 'completed';
  return (
    <TableRow className={isPublished ? 'bg-emerald-50/40 hover:!bg-emerald-50/70' : undefined}>
      {canPublish && (
        <TableCell className="align-middle">
          <input
            type="radio"
            name="audit-run-publish-select"
            aria-label={`Select run ${r.name} for publish`}
            checked={isSelected}
            onChange={onSelect}
            disabled={!selectable}
            className="h-4 w-4 cursor-pointer accent-brand-royal disabled:cursor-not-allowed disabled:opacity-40"
          />
        </TableCell>
      )}
      <TableCell>
        <div className="flex items-center gap-2">
          <Link
            to="/runs/$id"
            params={{ id: r.id }}
            className="font-medium text-brand-navy underline-offset-4 hover:underline"
          >
            {r.name}
          </Link>
          {isPublished && (
            <Badge className="border-transparent bg-emerald-600 text-white hover:bg-emerald-600/90">
              Published
            </Badge>
          )}
        </div>
        <div className="text-xs text-brand-slate">{formatDateTime(r.started_at)}</div>
      </TableCell>
      <TableCell>
        <StatusBadge status={r.status} pendingFindings={r.pending_findings} />
      </TableCell>
      <TableCell className="font-mono text-xs text-brand-slate">
        {r.rule_set_id}@v{r.rule_set_version}
      </TableCell>
      <TableCell className="text-right font-mono">
        {r.transaction_count.toLocaleString()}
      </TableCell>
      <TableCell className="text-right font-mono">
        {r.finding_count.toLocaleString()}
      </TableCell>
      <TableCell className="text-right font-mono text-brand-slate">
        {formatReviewedProgress(r.reviewed_findings, r.finding_count)}
      </TableCell>
      <TableCell className="text-right font-mono text-emerald-700">
        {formatCurrency(r.approved_recovery)}
      </TableCell>
      <TableCell className="text-right font-mono text-amber-700">
        {formatCurrency(r.approved_compliance_risk)}
      </TableCell>
    </TableRow>
  );
}

function ConfirmBar({
  message,
  confirmLabel,
  working,
  onConfirm,
  onCancel,
}: {
  message: string;
  confirmLabel: string;
  working: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}) {
  return (
    <div className="flex flex-wrap items-center gap-2 rounded-md border border-emerald-200 bg-emerald-50/60 px-3 py-2">
      <p className="text-sm text-brand-navy">{message}</p>
      <div className="flex items-center gap-2">
        <Button type="button" size="sm" onClick={onConfirm} disabled={working}>
          {working ? 'Working…' : confirmLabel}
        </Button>
        <Button type="button" size="sm" variant="ghost" onClick={onCancel} disabled={working}>
          Cancel
        </Button>
      </div>
    </div>
  );
}

function formatReviewedProgress(reviewed: number, total: number): string {
  if (total === 0) return '—';
  const pct = Math.round((reviewed / total) * 100);
  return `${reviewed.toLocaleString()}/${total.toLocaleString()} (${pct}%)`;
}

function ArchiveButton({ engagement }: { engagement: EngagementOut }) {
  const { userKey, getIdToken } = useAuth();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [confirming, setConfirming] = useState(false);

  const archive = useMutation({
    mutationFn: async () => archiveEngagement(engagement.id, (await getIdToken() ?? '')),
    onSuccess: () => {
      setConfirming(false);
      qc.invalidateQueries({ queryKey: ['engagements', engagement.id, userKey] });
      qc.invalidateQueries({ queryKey: ['engagements', 'by-account'] });
      navigate({
        to: '/customers/$slug',
        params: { slug: engagement.customer_slug },
      });
    },
  });

  if (!confirming) {
    return (
      <Button
        type="button"
        size="sm"
        variant="outline"
        onClick={() => setConfirming(true)}
      >
        Archive
      </Button>
    );
  }

  return (
    <div className="flex items-center gap-2">
      <span className="text-xs text-brand-slate">Archive this engagement?</span>
      <Button
        type="button"
        size="sm"
        variant="destructive"
        disabled={archive.isPending}
        onClick={() => archive.mutate()}
      >
        {archive.isPending ? 'Archiving…' : 'Confirm'}
      </Button>
      <Button
        type="button"
        size="sm"
        variant="ghost"
        disabled={archive.isPending}
        onClick={() => setConfirming(false)}
      >
        Cancel
      </Button>
      {archive.isError && (
        <span className="text-xs text-destructive">archive failed — see console</span>
      )}
    </div>
  );
}

function StatusBadge({
  status,
  pendingFindings,
}: {
  status: AuditRunOut['status'];
  pendingFindings: number;
}) {
  if (status === 'completed') {
    // The engine finished, but the practitioner hasn't classified
    // every finding yet — surface that as "in review" so the row
    // doesn't read as fully shippable. Amber, distinct from
    // completed-green and failed-red.
    if (pendingFindings > 0) {
      return (
        <Badge className="border-transparent bg-amber-500 text-white hover:bg-amber-500/90">
          in review
        </Badge>
      );
    }
    return (
      <Badge className="border-transparent bg-emerald-600 text-white hover:bg-emerald-600/90">
        completed
      </Badge>
    );
  }
  const variant = status === 'failed' ? 'destructive' : 'secondary';
  return <Badge variant={variant}>{status}</Badge>;
}
